import { FC, useEffect, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from "@mui/material";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import greyDialogTitleSx from "../Dialog/greyDialogTitleSx";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import LocationSelect from "./pickers/LocationSelect";
import SkuSelect from "./pickers/SkuSelect";
import { useUnitName, unitOptionTokens, factorOf, baseUnitNameOf } from "./pickers/useUnitName";
import {
  addRequisition,
  approveRequisition,
  createTransferFromRequisition,
  fetchRequisitionById,
  rejectRequisition,
  submitRequisition,
  updateRequisition,
  reopenRequisition,
  fetchRequisitionStockCheck,
} from "../../store/requisitionStore/requisitionThunk";

const unitOptionsFor = unitOptionTokens;
const emptyRow = () => ({ sku: null as any, skuId: "", unit: "", qtyRequested: "", qtyApproved: "", qtyIssued: 0 });

const RequisitionForm: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const { status } = useAppSelector((state) => state.requisitions);
  const unitName = useUnitName();

  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [items, setItems] = useState<any[]>([emptyRow()]);
  const [docStatus, setDocStatus] = useState("draft");
  const [reqNumber, setReqNumber] = useState<string | null>(null);
  const [stockCheck, setStockCheck] = useState<any>(null);
  // A DRAFT transfer blocks creating another (qtyIssued advances only on dispatch).
  // A DISPATCHED (in-transit) transfer does NOT block — the remaining approved qty
  // can still be issued in a new transfer (partial fulfilment across shipments).
  const [draftTransferId, setDraftTransferId] = useState<string | null>(null);
  const [draftTransferNumber, setDraftTransferNumber] = useState<string | null>(null);
  const [inTransitTransferId, setInTransitTransferId] = useState<string | null>(null);
  const [inTransitTransferNumber, setInTransitTransferNumber] = useState<string | null>(null);
  // Reopen lock: a fulfilled/approved requisition can only be reopened once its
  // transfer is reversed/cancelled and nothing is still issued. The backend tells
  // us so we can disable the button proactively.
  const [canReopen, setCanReopen] = useState(false);
  const [reopenBlockReason, setReopenBlockReason] = useState<string>("");
  const [helpOpen, setHelpOpen] = useState(false);

  const isDraft = !isEditing || docStatus === "draft";
  const isSubmitted = docStatus === "submitted";
  const isApproved = docStatus === "approved" || docStatus === "partially_fulfilled";
  // Approve/reject/issue belong to the fulfiller; the store holds only the user's own locations.
  const { locations } = useAppSelector((state) => state.locations);
  const canFulfil = (locations || []).some((l: any) => l._id === toLocationId);

  // When pending approval / approved, check the fulfiller actually has the stock.
  useEffect(() => {
    if (id && (isSubmitted || isApproved)) {
      dispatch(fetchRequisitionStockCheck(id))
        .unwrap()
        .then((data: any) => setStockCheck(data))
        .catch(() => setStockCheck(null));
    } else {
      setStockCheck(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, docStatus]);

  const shortLines = (stockCheck?.lines || []).filter((l: any) => l.tracked && !l.sufficient);

  useEffect(() => {
    if (isEditing && id) {
      dispatch(fetchRequisitionById(id))
        .unwrap()
        .then((req: any) => {
          setFromLocationId(req.fromLocationId?._id || req.fromLocationId || "");
          setToLocationId(req.toLocationId?._id || req.toLocationId || "");
          setDocStatus(req.status || "draft");
          setReqNumber(req.reqNumber || null);
          setDraftTransferId(req.draftTransferId || null);
          setDraftTransferNumber(req.draftTransferNumber || null);
          setInTransitTransferId(req.inTransitTransferId || null);
          setInTransitTransferNumber(req.inTransitTransferNumber || null);
          setCanReopen(!!req.canReopen);
          setReopenBlockReason(req.reopenBlockReason || "");
          setItems(
            (req.items || []).map((it: any) => ({
              _id: it._id, // the requisition line id — approve matches qtyApproved by this exact line
              sku: it.skuId,
              skuId: it.skuId?._id || it.skuId,
              unit: it.unit || "",
              qtyRequested: it.qtyRequested ?? "",
              // Default the approve qty to the requested qty. A submitted requisition
              // has qtyApproved=0 in the DB, and `??` won't fall back on 0 — so use
              // the saved approved qty only when it's a real (non-zero) value.
              qtyApproved: it.qtyApproved || it.qtyRequested || "",
              qtyIssued: it.qtyIssued ?? 0,
            }))
          );
        })
        .catch((e: any) => toast.error(e?.message || "Failed to load requisition"));
    }
  }, [id, isEditing, dispatch]);

  const updateRow = (idx: number, patch: any) => setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const onSkuChange = (idx: number, sku: any) => updateRow(idx, { sku, skuId: sku?._id || "", unit: unitOptionsFor(sku)[0] || "" });

  const onSave = async () => {
    if (!fromLocationId || !toLocationId) return toast.error("Select requester and fulfiller locations");
    const payload = {
      fromLocationId,
      toLocationId,
      items: items
        .filter((r) => r.skuId && Number(r.qtyRequested) > 0)
        .map((r) => ({ skuId: r.skuId, qtyRequested: Number(r.qtyRequested), unit: r.unit || null })),
    };
    if (!payload.items.length) return toast.error("Add at least one item");
    try {
      if (isEditing && id) {
        await dispatch(updateRequisition({ id, payload })).unwrap();
        navigate("/inventory/requisitions");
      } else {
        const created = await dispatch(addRequisition(payload)).unwrap();
        navigate(`/inventory/requisitions/${created._id}`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to save");
    }
  };

  const runAction = async (thunkResultPromise: Promise<any>, after?: (res: any) => void) => {
    try {
      const res = await thunkResultPromise;
      if (after) after(res);
      else navigate("/inventory/requisitions");
    } catch (e: any) {
      toast.error(e?.message || "Action failed");
    }
  };

  const onSubmit = () => runAction(dispatch(submitRequisition(id!)).unwrap());
  const onApprove = () =>
    runAction(
      dispatch(
        approveRequisition({ id, items: items.map((r) => ({ _id: r._id, skuId: r.skuId, qtyApproved: Number(r.qtyApproved) || 0 })) })
      ).unwrap()
    );
  const onReject = () => {
    const reason = window.prompt("Reason for rejection?") || "";
    runAction(dispatch(rejectRequisition({ id, reason })).unwrap());
  };
  const onCreateTransfer = () =>
    runAction(dispatch(createTransferFromRequisition(id!)).unwrap(), (res: any) =>
      navigate(res?._id ? `/inventory/transfers/${res._id}` : "/inventory/transfers")
    );

  const loading = status === ApiStatus.LOADING;

  return (
    <Box sx={{ p: 2, pr: 0, mr: "-3px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate("/inventory/requisitions")}>
            <NavigateBeforeOutlinedIcon />
          </IconButton>
          <Typography variant="h6">
            {isEditing ? `Requisition${reqNumber ? ` ( ${reqNumber} )` : ""}` : "New Requisition"}
          </Typography>
          <Tooltip title="How to raise a requisition">
            <IconButton size="small" onClick={() => setHelpOpen(true)}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#7a8290" }} />
            </IconButton>
          </Tooltip>
          {isEditing && <Chip size="small" label={(docStatus || "").replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())} />}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isDraft && (
            <LoadingButton variant="contained" loading={loading} onClick={onSave}>
              {isEditing ? "Save" : "Create"}
            </LoadingButton>
          )}
          {isEditing && docStatus === "draft" && <Button variant="contained" onClick={onSubmit}>Submit</Button>}
          {isSubmitted && canFulfil && (
            <>
              <LoadingButton color="success" variant="contained" loading={loading} onClick={onApprove}>Approve</LoadingButton>
              <Button color="error" variant="outlined" onClick={onReject}>Reject</Button>
            </>
          )}
          {isApproved && canFulfil && (
            <>
              <Tooltip
                title={
                  draftTransferId
                    ? `Transfer ${draftTransferNumber || ""} is still a draft. Dispatch or cancel it before creating another.`
                    : ""
                }
              >
                {/* span wrapper so the tooltip still shows on a disabled button */}
                <span>
                  <Button variant="contained" onClick={onCreateTransfer} disabled={!!draftTransferId}>
                    Create Transfer
                  </Button>
                </span>
              </Tooltip>
              <Button color="error" variant="outlined" onClick={onReject}>Reject</Button>
            </>
          )}
          {isEditing && docStatus !== "draft" && (
            <Tooltip title={!canReopen ? reopenBlockReason || "This requisition cannot be reopened." : ""}>
              {/* span wrapper so the tooltip still shows on a disabled button */}
              <span>
                <Button
                  color="warning"
                  variant="outlined"
                  disabled={!canReopen}
                  onClick={() => {
                    if (window.confirm("Reopen this requisition to draft for re-testing? Approvals and issued quantities are cleared — reverse any transfers first.")) {
                      runAction(dispatch(reopenRequisition(id!)).unwrap());
                    }
                  }}
                >
                  Reopen
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* DRAFT transfer — blocks creating another until dispatched or cancelled. */}
      {isApproved && draftTransferId && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: "1px solid #f0c36d", backgroundColor: "#fff8e6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: "#7a5c00" }}>
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
            <span>
              Transfer <b>{draftTransferNumber || ""}</b> is still a <b>draft</b> for this requisition. Dispatch or
              cancel it before creating another — issued quantity is only counted once it's dispatched.
            </span>
          </Box>
          <Button
            variant="contained"
            size="small"
            sx={{ flexShrink: 0 }}
            onClick={() => navigate(`/inventory/transfers/${draftTransferId}`)}
          >
            Go to Transfer
          </Button>
        </Box>
      )}

      {/* DISPATCHED (in-transit) transfer — informational; creating another for the
          remaining approved qty is allowed. */}
      {isApproved && !draftTransferId && inTransitTransferId && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: "1px solid #9cc3ff", backgroundColor: "#eef5ff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: "#1f4e8a" }}>
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
            <span>
              Transfer <b>{inTransitTransferNumber || ""}</b> is <b>in transit</b>. You can still create another
              transfer for the remaining approved quantity.
            </span>
          </Box>
          <Button
            variant="outlined"
            size="small"
            sx={{ flexShrink: 0 }}
            onClick={() => navigate(`/inventory/transfers/${inTransitTransferId}`)}
          >
            Go to Transfer
          </Button>
        </Box>
      )}

      {shortLines.length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: "1px solid #f0c36d", backgroundColor: "#fff8e6", display: "flex", gap: 1 }}>
          <InfoOutlinedIcon sx={{ color: "#b8860b", fontSize: 20, mt: "2px" }} />
          <Box sx={{ fontSize: 13, color: "#7a5c00" }}>
            <b>{stockCheck?.fulfillerName || "The fulfiller"} doesn't have enough stock to issue this requisition.</b>{" "}
            You can still {isSubmitted ? "approve" : "create the transfer"}, but <b>Dispatch will fail</b> (or post a flagged oversell) until you stock the fulfiller.
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {shortLines.map((l: any) => (
                <li key={l.skuId}>
                  <b>{l.name}</b>: need {l.needed} {l.baseUnit}, available {l.available} {l.baseUnit}
                  {" "}(short {Math.max(0, Number(l.needed) - Number(l.available))} {l.baseUnit})
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      )}

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ ...greyDialogTitleSx, fontSize: 18, fontWeight: 600 }}>How to raise a requisition</DialogTitle>
        <DialogContent dividers>
          <ol style={{ margin: 0, paddingLeft: 18, color: "#3c4350", lineHeight: 1.7 }}>
            <li>Set the <b>requester</b> (who needs the goods) and the <b>fulfiller</b> (who will issue them).</li>
            <li>Add items with the <b>unit</b> and <b>quantity requested</b>.</li>
            <li><b>Submit</b> for approval. The fulfiller <b>Approves</b> (optionally changing the approved qty) or <b>Rejects</b>.</li>
            <li>From an approved requisition click <b>Create Transfer</b> to issue the goods, then <b>Dispatch</b> &amp; <b>Receive</b> it on the Transfers tab.</li>
            <li><b>No duplicate drafts:</b> "Create Transfer" is disabled only while a transfer for this requisition is still a <b>draft</b> (not yet dispatched). Dispatch or cancel it first.</li>
            <li><b>Partial fulfilment across shipments:</b> issued quantity is counted on <b>dispatch</b>. Once a transfer is <b>dispatched</b> (even while it's in transit), the requisition becomes <i>Partially Fulfilled</i> and you can immediately create the next transfer for the remaining approved quantity.</li>
          </ol>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          {/* Exclude the chosen fulfiller so requester/fulfiller can't be the same. */}
          <LocationSelect
            value={fromLocationId}
            onChange={(v) => { setFromLocationId(v); if (v && v === toLocationId) setToLocationId(""); }}
            label="Requester (from)"
            activeOnly={false}
            excludeId={toLocationId}
            allLocations={!isDraft}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {/* Exclude the chosen requester so requester/fulfiller can't be the same. */}
          <LocationSelect
            value={toLocationId}
            onChange={(v) => { setToLocationId(v); if (v && v === fromLocationId) setFromLocationId(""); }}
            label="Fulfiller (to)"
            activeOnly={false}
            excludeId={fromLocationId}
            allLocations
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 2.5, display: "flex", alignItems: "center", gap: 0.5 }}>
          Items requested
          <Tooltip title="Pick the item, the unit, and how much you need. The grey line shows the total in the item's base unit.">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7a8290" }} />
          </Tooltip>
          {isDraft && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setItems((rows) => [emptyRow(), ...rows])}
              sx={{ ml: "auto", color: "#488dff", backgroundColor: "#fff", border: "0.5px solid #488dff", borderRadius: "7px", textTransform: "none", "&:hover": { backgroundColor: "#f5f9ff", border: "0.5px solid #488dff" } }}>
              Add item
            </Button>
          )}
        </Typography>
        {items.map((row, idx) => {
          const factor = factorOf(row.sku, row.unit);
          const baseName = baseUnitNameOf(row.sku, unitName);
          const qtyNum = Number(row.qtyRequested);
          const baseQty = factor > 0 && qtyNum > 0 ? factor * qtyNum : 0;
          return (
            <Box key={idx} sx={{ mb: 1.5, pb: 1, borderBottom: "1px dashed #e6e8ec" }}>
              <Grid container spacing={1} sx={{ alignItems: "center" }}>
                <Grid item xs={12} sm={4}>
                  <SkuSelect value={row.skuId} onChange={(sku) => onSkuChange(idx, sku)} />
                </Grid>
                <Grid item xs={4} sm={1.5}>
                  <TextField select fullWidth size="small" label="Unit" value={row.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })} disabled={!isDraft}>
                    {unitOptionsFor(row.sku).map((u) => <MenuItem key={u} value={u}>{unitName(u)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4} sm={1.5}>
                  <TextField fullWidth size="small" type="number" onWheel={(e) => (e.target as HTMLInputElement).blur()} label="Qty requested" value={row.qtyRequested} onChange={(e) => updateRow(idx, { qtyRequested: e.target.value })} disabled={!isDraft} />
                </Grid>
                {/* Approved & Issued stay visible for ALL non-draft statuses
                    (submitted → fulfilled / rejected) so the record always shows
                    what was approved and issued. Approved is editable only while
                    the requisition is awaiting approval (submitted). */}
                {!isDraft && (
                  <Grid item xs={4} sm={1.5}>
                    <TextField fullWidth size="small" type="number" onWheel={(e) => (e.target as HTMLInputElement).blur()} label="Qty approved" value={row.qtyApproved} onChange={(e) => updateRow(idx, { qtyApproved: e.target.value })} disabled={!isSubmitted} />
                  </Grid>
                )}
                {!isDraft && (
                  <Grid item xs={4} sm={1.5}>
                    <TextField fullWidth size="small" label="Qty issued" value={row.qtyIssued} disabled />
                  </Grid>
                )}
                {isDraft && (
                  <Grid item xs={2} sm={0.6}>
                    <IconButton size="small" onClick={() => setItems((rows) => rows.filter((_, i) => i !== idx))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
              {factor > 0 && qtyNum > 0 && (
                <Typography sx={{ fontSize: 12, color: "#5b6472", mt: 0.5 }}>
                  Requesting: {qtyNum} {unitName(row.unit)} × {factor} {baseName} = <b>{baseQty.toLocaleString()} {baseName}</b>
                </Typography>
              )}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default RequisitionForm;
